import React from 'react';
import { Box, Container, Typography, Button, Stack, IconButton, Divider, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { isHouseproBookingEnabled } from '../utils/featureFlags';
import FloatingActionButton from '../components/FloatingActionButton';
import { useNavigate } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import { Instagram, Facebook, LinkedIn } from "@mui/icons-material";
import LockIcon from '@mui/icons-material/Lock';



const TiktokIcon = ({ color = "#000000" }) => {
  return (
    <svg
      fill="white"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 50 50"
      width="24"
      height="24"
    >
      <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
    </svg>
  );
};

const Footer = () => {
  const theme = useTheme();
  
  return (

  <Box
    component="footer"
    sx={{
      bgcolor: 'primary.dark',
      color: 'common.white',
      py: { xs: 2, sm: 3, md: 4 },
      mt: { xs: 4, sm: 6, md: 8 },
      position: 'static',
      width: '100%',
      boxShadow: 3,
      fontSize: { xs: theme.typography.body2.fontSize, sm: theme.typography.body1.fontSize, md: theme.typography.subtitle1.fontSize, lg: theme.typography.h6.fontSize, xl: theme.typography.h5.fontSize },
    }}
  >
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 }, maxWidth: 'none' }}>
      {/* Service-area links — internal linking for local SEO */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 },
          justifyContent: 'center',
          mb: 2,
          pb: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {[
          { to: '/service-areas', label: 'All Service Areas' },
          { to: '/house-cleaning/orlando', label: 'Orlando' },
          { to: '/house-cleaning/tampa', label: 'Tampa' },
          { to: '/house-cleaning/sarasota', label: 'Sarasota' },
          { to: '/house-cleaning/naples', label: 'Naples' },
          { to: '/house-cleaning/fort-myers', label: 'Fort Myers' },
          { to: '/house-cleaning/fort-lauderdale', label: 'Fort Lauderdale' },
          { to: '/house-cleaning/miami', label: 'Miami' },
        ].map((link) => (
          <Button
            key={link.to}
            component={RouterLink}
            to={link.to}
            color="inherit"
            size="small"
            sx={{ opacity: 0.85, textTransform: 'none', minWidth: 'auto', px: 1, '&:hover': { opacity: 1 } }}
          >
            {link.label}
          </Button>
        ))}
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'column', md: 'row' }}
        spacing={{ xs: 2, sm: 2.5, md: 3 }}
        alignItems="center"
        justifyContent="space-between"
        divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)', display: { xs: 'none', md: 'block' } }} />}
        sx={{ textAlign: { xs: 'center', md: 'left' }, minHeight: { xs: 'auto', md: '60px' } }}
      >
        {/* Copyright */}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            opacity: 0.8,
            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '1.1rem', xl: '1.2rem' }
          }}
        >
          © {new Date().getFullYear()} 5 Fairies Cleaning Services
        </Typography>

        {/* Become a Cleaner - Only show when Housepro feature flag is disabled */}
        { (
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ justifyContent: { xs: 'center', md: 'flex-start' }, width: { xs: '100%', md: 'auto' } }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                opacity: 0.8, 
                display: { xs: 'none', sm: 'block' },
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '1.1rem', xl: '1.2rem' }
              }}
            >
              Join our team of professionals
            </Typography>
            <Button
              component={RouterLink}
              to="/maid-onboarding"
              variant="outlined"
              size="small"
              color="inherit"
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '1.1rem', xl: '1.2rem' },
                px: { xs: 2, sm: 3 },
                py: { xs: 0.5, sm: 1 },
                minHeight: { xs: '36px', sm: '40px' },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'common.white',
                  background: 'rgba(255,255,255,0.1)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Typography 
                variant="button" 
                sx={{ 
                  display: { xs: 'none', sm: 'inline' }, 
                  fontSize: 'inherit'
                }}
              >
                Become a Cleaner
              </Typography>
              <Typography 
                variant="button" 
                sx={{ 
                  display: { xs: 'inline', sm: 'none' }, 
                  fontSize: 'inherit'
                }}
              >
                Become a Cleaner
              </Typography>
            </Button>
          </Stack>
        )}

        {/* Social Links */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: { xs: 1, sm: 1.5 },
          justifyContent: { xs: 'center', md: 'flex-end' }, 
          width: { xs: '100%', md: 'auto' },
          maxWidth: { xs: '280px', sm: '100%' },
          mx: { xs: 'auto', md: 0 }
        }}>
          <IconButton 
            component="a" 
            href="https://www.facebook.com/profile.php?id=61578123928147" 
            target="_blank" 
            rel="noopener" 
            color="inherit" 
            size="small"
            aria-label="Facebook"
            sx={{ 
              opacity: 0.8,
              minWidth: { xs: '40px', sm: '44px' },
              minHeight: { xs: '40px', sm: '44px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': { opacity: 1, background: 'rgba(255,255,255,0.1)', transform: 'translateY(-1px)' }
            }}
          >
            <Facebook fontSize="small" />
          </IconButton>
          <IconButton 
            component="a" 
            href="https://www.instagram.com/5fairiescleaning"
            target="_blank"
            rel="noopener"
            color="inherit"
            size="small"
            aria-label="Instagram"
            sx={{ 
              opacity: 0.8,
              minWidth: { xs: '40px', sm: '44px' },
              minHeight: { xs: '40px', sm: '44px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': { opacity: 1, background: 'rgba(255,255,255,0.1)', transform: 'translateY(-1px)' }
            }}
          >
            <Instagram fontSize="small" />
          </IconButton>

          <IconButton 
            component="a" 
            href="https://www.linkedin.com/company/107950831/"
            target="_blank"
            rel="noopener"
            color="inherit" 
            size="small"
            aria-label="LinkedIn"
            sx={{ 
              opacity: 0.8,
              minWidth: { xs: '40px', sm: '44px' },
              minHeight: { xs: '40px', sm: '44px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': { opacity: 1, background: 'rgba(255,255,255,0.1)', transform: 'translateY(-1px)' }
            }}
          >
            <LinkedIn fontSize="small" />
          </IconButton>          
          <IconButton 
            component="a" 
            href="https://www.tiktok.com/@5fairiescleaning"
            target="_blank"
            rel="noopener"
            color="inherit" 
            size="small"
            aria-label="TikTok"
            sx={{ 
              opacity: 0.8,
              minWidth: { xs: '40px', sm: '44px' },
              minHeight: { xs: '40px', sm: '44px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': { opacity: 1, background: 'rgba(255,255,255,0.1)', transform: 'translateY(-1px)' }
            }}
          >
            <TiktokIcon fontSize="small" />
          </IconButton>
          <IconButton 
            component="a" 
            href="https://x.com/5FairiesClean" 
            target="_blank" 
            rel="noopener" 
            color="inherit" 
            size="small"
            aria-label="X (Twitter)"
            sx={{ 
              opacity: 0.8,
              minWidth: { xs: '40px', sm: '44px' },
              minHeight: { xs: '40px', sm: '44px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': { opacity: 1, background: 'rgba(255,255,255,0.1)', transform: 'translateY(-1px)' }
            }}
          >
            {/* X (Twitter) SVG icon */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.53 3H21.5L14.88 10.39L22.68 21H16.16L11.36 14.67L5.88 21H1.9L8.92 13.13L1.43 3H8.09L12.41 8.74L17.53 3ZM16.36 19H18.18L7.7 4.89H5.76L16.36 19Z" fill="currentColor"/>
            </svg>
          </IconButton>
          <IconButton 
            component="a" 
            href="https://www.yelp.fr/biz/5-fairies-cleaning-orlando" 
            target="_blank" 
            rel="noopener" 
            color="inherit" 
            size="small"
            aria-label="Yelp"
            sx={{ 
              opacity: 0.8,
              minWidth: { xs: '40px', sm: '44px' },
              minHeight: { xs: '40px', sm: '44px' },
              transition: 'all 0.2s ease-in-out',
              '&:hover': { opacity: 1, background: 'rgba(255,255,255,0.1)', transform: 'translateY(-1px)' }
            }}
          >
            {/* Yelp SVG icon */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.7 2.1c-.4-.7-1.4-.7-1.8 0l-2.2 3.8c-.2.3-.2.7 0 1l2.2 3.8c.4.7 1.4.7 1.8 0l2.2-3.8c.2-.3.2-.7 0-1l-2.2-3.8zm7.2 4.7c-.2-.4-.7-.5-1.1-.3l-4.1 2.1c-.3.2-.5.6-.3 1l2.1 4.1c.2.4.7.5 1.1.3l4.1-2.1c.3-.2.5-.6.3-1l-4.1-2.1c-.4-.2-.9-.1-1.1.3zm-15.8 0l-2.1 4.1c-.2.4 0 .8.3 1l4.1 2.1c.4.2.9.1 1.1-.3l2.1-4.1c.2-.4 0-.8-.3-1l-4.1-2.1c-.4-.2-.9-.1-1.1.3zm15.8 10.4l-4.1-2.1c-.4-.2-.9-.1-1.1.3l-2.1 4.1c-.2.4 0 .8.3 1l4.1 2.1c.4.2.9.1 1.1-.3l2.1-4.1c.2-.4 0-.8-.3-1zm-15.8 0c-.2.4 0 .8.3 1l4.1 2.1c.4.2.9.1 1.1-.3l2.1-4.1c.2-.4 0-.8-.3-1l-4.1-2.1c-.4-.2-.9-.1-1.1.3z" fill="#d32323"/>
            </svg>
          </IconButton>
        </Box>
        {/* Admin Login Button */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: { xs: 'center', md: 'flex-end' }, width: { xs: '100%', md: 'auto' } }}>
          <Button
            component={RouterLink}
            to="/login"
            variant="text"
            size="small"
            color="inherit"
            startIcon={<LockIcon fontSize="small" />}
            sx={{ 
              opacity: 0.7, 
              fontSize: { xs: theme.typography.caption.fontSize, sm: theme.typography.body2.fontSize, md: theme.typography.body1.fontSize, lg: theme.typography.subtitle2.fontSize, xl: theme.typography.subtitle1.fontSize }, 
              textTransform: 'none', 
              px: { xs: 1, sm: 2 },
              '&:hover': { opacity: 1 } 
            }}
          >
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' } }}>Admin Login</Typography>
            <Typography variant="caption" sx={{ display: { xs: 'inline', sm: 'none' } }}>Admin</Typography>
          </Button>
        </Stack>
      </Stack>

    </Container>
  </Box>
  );
};



export default Footer; 